import { NS } from '@ownyou/shared-types';
import type { ChangeAlert } from '../workers/change-detector';
import { appendChangeAlertInboxEvent, markInboxEventsRead } from '../lib/inbox/events';

export interface NotificationSettings {
  enabled: boolean;
  pushEnabled: boolean;
}

export class NotificationService {
  private alerts: ChangeAlert[] = [];
  private listeners: Set<() => void> = new Set();
  private pendingDurableWrites: Promise<unknown>[] = [];

  constructor(private store: import('@ownyou/memory-store').MemoryStore, private userId: string) {}

  async loadSettings(): Promise<NotificationSettings> {
    const ns = NS.notificationsSettings(this.userId);
    const settings = await this.store.get(ns, 'settings');
    return (settings as NotificationSettings | null) ?? { enabled: true, pushEnabled: false };
  }

  async saveSettings(settings: NotificationSettings): Promise<void> {
    const ns = NS.notificationsSettings(this.userId);
    await this.store.put(ns, 'settings', settings);
  }

  addAlert(alert: ChangeAlert): void {
    this.alerts.push(alert);
    // Evict oldest alerts beyond 100 to prevent unbounded growth (M15)
    if (this.alerts.length > 100) {
      this.alerts = this.alerts.slice(-100);
    }
    this.trackDurableWrite(appendChangeAlertInboxEvent({
      store: this.store,
      userId: this.userId,
      alert,
    }));
    this.notify();
  }

  getAlerts(): ChangeAlert[] {
    return [...this.alerts];
  }

  markAllRead(): void {
    this.trackDurableWrite(markInboxEventsRead(this.store, this.userId));
    this.notify();
  }

  async flushDurableWrites(): Promise<void> {
    await Promise.all(this.pendingDurableWrites);
    this.pendingDurableWrites = [];
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    for (const listener of this.listeners) listener();
  }

  private trackDurableWrite(write: Promise<unknown>): void {
    this.pendingDurableWrites.push(write.catch((error) => {
      console.warn('[NotificationService] Durable Inbox write failed:', error);
    }));
  }
}
